import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Editor } from '@tiptap/react'
import { EditorType } from '../types'
import {
  collapseEmptyParagraphMarkers,
  expandEmptyParagraphMarkers,
  normalize,
} from '../../../../../../utils/multilineText'
import {
  getMarkdownFromRichEditor,
  getMarkdownFromSourceEditor,
  insertLinkFromClipboard,
  markdownToSourceDoc,
} from '../../../richtext'
import { isImageFile, UploadedFile } from '../../../../../../utils/file'
import { insertMarkdownImages } from '../markdownSyntax'
import TenantStorage from '../../../../../../utils/storage'
import store from '../../../../../../store'
import { showAlert } from '../../../../../../store/globalAlert'

type UseMultilineEditorParams = {
  editorType: EditorType
  initialized: boolean
  value: string
  dataLockVersion?: number
  setValue?: Function
  formatMessage: (descriptor: { id: string }) => string
}

const executeUpload = async (file): Promise<UploadedFile> => {
  const response = await TenantStorage.uploadFile(file)
  return {
    url: response.json.downloadUrl,
    name: file.name,
    mimeType: file.type,
  }
}

export const useMultilineEditor = ({
  editorType,
  initialized,
  value,
  dataLockVersion,
  setValue,
  formatMessage,
}: UseMultilineEditorParams) => {
  // 各 View が useEditor で生成した Editor インスタンスをマウント時に登録する。
  // モード切替で非アクティブ側の View はアンマウントされるため、同時に生存するのは原則1つ。
  const [richTextEditor, setRichTextEditor] = useState<Editor | null>(null)
  const [markdownSourceEditor, setMarkdownSourceEditor] =
    useState<Editor | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [previewText, setPreviewText] = useState<string>(
    collapseEmptyParagraphMarkers(normalize(value || ''))
  )

  // 現在のMarkdown値のキャッシュ。空段落マーカーを畳んだ正規形で保持する。
  const valueRef = useRef<string>(
    collapseEmptyParagraphMarkers(normalize(value || ''))
  )

  const notifyChange = useCallback(
    (val: string) => {
      // normalize: &nbsp; → \xA0 に統一し raw/decoded の不一致を防ぐ。
      // collapse: 空段落マーカー前後の余分な空行を除去し、ソース表示とリッチ表示の空行数を揃える。
      const collapsed = collapseEmptyParagraphMarkers(normalize(val))
      valueRef.current = collapsed
      setValue?.(collapsed)
      setPreviewText(collapsed)
    },
    [setValue]
  )

  const debouncedNotifyChange = useMemo(
    () => _.debounce(notifyChange, 100),
    [notifyChange]
  )

  // マークダウン文字列を、指定モードのエディタに反映する。
  // emitUpdate: false により、setContent 起因の onUpdate 発火（=親への不要な setValue 通知）を抑止する。
  // 通知が必要なケース（テンプレート挿入等）は呼び出し側で明示的に notifyChange を呼ぶ想定。
  const applyMarkdownToEditor = useCallback(
    (md: string, type: EditorType) => {
      if (type === EditorType.Draft && richTextEditor) {
        // 畳んだマーカーを展開して marked が段落区切りとして認識できる形にする
        richTextEditor.commands.setContent(expandEmptyParagraphMarkers(md), {
          contentType: 'markdown',
          emitUpdate: false,
        })
      } else if (type === EditorType.Markdown && markdownSourceEditor) {
        markdownSourceEditor.commands.setContent(
          markdownToSourceDoc(collapseEmptyParagraphMarkers(md)),
          { emitUpdate: false }
        )
      }
    },
    [richTextEditor, markdownSourceEditor]
  )

  // View がエディタを登録した直後に現在値を反映する。
  // モード切替時は新 View のマウントで新エディタが登録されるので、その時点でもここが走る。
  useEffect(() => {
    if (!initialized) return
    applyMarkdownToEditor(valueRef.current, editorType)
    if (editorType === EditorType.Draft) {
      richTextEditor?.commands.focus('start')
    } else {
      markdownSourceEditor?.commands.focus('start')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- エディタ登録のタイミングだけで反映したい
  }, [initialized, richTextEditor, markdownSourceEditor])

  // 親からのprops.value更新を検知して、アクティブなエディタに反映
  useEffect(() => {
    const newValue = collapseEmptyParagraphMarkers(normalize(value || ''))
    if (newValue === valueRef.current) return
    valueRef.current = newValue
    setPreviewText(newValue)
    applyMarkdownToEditor(newValue, editorType)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props起因の更新のみ検知
  }, [value, dataLockVersion])

  const switchEditor = useCallback(() => {
    // モード切替前にアクティブエディタの最新マークダウンを valueRef に確保する。
    // 新エディタ側への反映は、新 View がマウントしてエディタを登録した後に
    // 上の useEffect（[initialized, richTextEditor, markdownSourceEditor]）で行われる。
    let currentMarkdown = valueRef.current
    if (editorType === EditorType.Draft && richTextEditor) {
      currentMarkdown = getMarkdownFromRichEditor(richTextEditor)
    } else if (editorType === EditorType.Markdown && markdownSourceEditor) {
      currentMarkdown = getMarkdownFromSourceEditor(markdownSourceEditor)
    }
    const collapsed = collapseEmptyParagraphMarkers(normalize(currentMarkdown))
    valueRef.current = collapsed
    setPreviewText(collapsed)
  }, [editorType, richTextEditor, markdownSourceEditor])

  const applyContent = useCallback(
    (text: string) => {
      notifyChange(text)
      applyMarkdownToEditor(text, editorType)
    },
    [notifyChange, applyMarkdownToEditor, editorType]
  )

  const hasContent = useCallback(() => !!valueRef.current, [])

  // --- Image upload ---

  const uploadImages = async (files): Promise<UploadedFile[]> => {
    const imgFiles = files.filter(file => isImageFile(file))
    if (files.length !== imgFiles.length) {
      store.dispatch(
        showAlert({
          message: formatMessage({
            id: 'editor.canNotUploadFile',
          }),
        })
      )
    }
    const uploadedFiles: UploadedFile[] = await Promise.all(
      imgFiles.map(file => executeUpload(file))
    )
    return uploadedFiles.filter(v => v.url)
  }

  const finalizeRichTextImageInsert = _.debounce(() => {
    if (!richTextEditor) return
    const md = getMarkdownFromRichEditor(richTextEditor)
    setLoading(false)
    notifyChange(md)
  }, 100)

  const insertImagesToRichText = (files: UploadedFile[]) => {
    files.forEach(file => {
      richTextEditor?.chain().focus().setImage({ src: file.url }).run()
    })
    finalizeRichTextImageInsert()
  }

  // エディタ登録前に呼ばれた場合の防御として、
  // エディタ不在時は valueRef のキャッシュを元に末尾へ追記する。
  const insertImagesToMarkdown = (files: UploadedFile[]) => {
    if (files.length === 0) {
      setLoading(false)
      return
    }
    const imageMarkdown = files.reduce(
      (acc, value) => acc + `![](${value.url})\n\n`,
      ''
    )
    const currentText = markdownSourceEditor
      ? getMarkdownFromSourceEditor(markdownSourceEditor)
      : valueRef.current
    const newText = currentText
      ? `${currentText}\n${imageMarkdown}`
      : imageMarkdown
    markdownSourceEditor?.commands.setContent(markdownToSourceDoc(newText), {
      emitUpdate: false,
    })
    setLoading(false)
    notifyChange(newText)
  }

  const onDropAcceptedDraft = async acceptedFiles => {
    const files = await uploadImages(acceptedFiles)
    insertImagesToRichText(files)
  }

  const onDropAcceptedMarkdown = async acceptedFiles => {
    const files = await uploadImages(acceptedFiles)
    insertImagesToMarkdown(files)
  }

  const onPaste = async event => {
    if (event.clipboardData.files.length) {
      const files = await uploadImages(Array.from(event.clipboardData.files))
      insertImagesToRichText(files)
    }
  }

  // Markdownソースエディタ用の画像ペースト。キャプチャ等のクリップボード画像を
  // アップロードし、ツールバーの画像挿入と同じくカーソル位置に挿入する。
  const onPasteIntoMarkdown = async event => {
    if (!event.clipboardData?.files.length) return
    const files = await uploadImages(Array.from(event.clipboardData.files))
    if (files.length === 0) return
    if (markdownSourceEditor) {
      // 挿入トランザクションで onUpdate が発火し、親への通知は onContentChange 経由で行われる
      insertMarkdownImages(markdownSourceEditor, files)
    } else {
      insertImagesToMarkdown(files)
    }
  }

  const onPasteCaptureIntoRichText = useCallback(
    event => {
      if (!richTextEditor || !event.clipboardData) return
      if (insertLinkFromClipboard(event.clipboardData, richTextEditor)) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [richTextEditor]
  )

  return {
    previewText,
    loading,
    switchEditor,
    applyContent,
    hasContent,
    onDropAcceptedDraft,
    onDropAcceptedMarkdown,
    onPaste,
    onPasteIntoMarkdown,
    onPasteCaptureIntoRichText,
    // View からの登録/通知用ハンドル
    registerRichTextEditor: setRichTextEditor,
    registerMarkdownSourceEditor: setMarkdownSourceEditor,
    onContentChange: debouncedNotifyChange,
  }
}
